import React, { useState, useEffect } from 'react';
import { Button, Dropdown, Badge } from 'react-bootstrap';
import { PlusCircle, PersonGear, Briefcase, GraphUp, CheckSquare, Gear, Pencil, Key, ChevronDown, GraphUpArrow } from 'react-bootstrap-icons';
import CreateProfileModal from '../../features/profiles/CreateProfileModal';
import ManageProfilesModal from '../../features/profiles/ManageProfilesModal';
import UserProfileEditModal from '../../features/profiles/UserProfileEditModal';
import ChangePasswordModal from '../../features/profiles/ChangePasswordModal';
import { getFlagEmoji } from '../utils/currency';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const ActivityType = {
    PROFILE_MANAGEMENT_MODAL_OPENED: "PROFILE_MANAGEMENT_MODAL_OPENED",
    PROFILE_MANAGEMENT_MODAL_CLOSED: "PROFILE_MANAGEMENT_MODAL_CLOSED",
};

const SideBar = ({ profiles, activeProfileId, onProfileSelect, onHomeSelect, handleCreateProfile, handleUpdateProfile, handleDeleteProfile, onProfileVisibilityChange, showCreateProfileModalFromHome, setShowCreateProfileModalFromHome }) => {
    const [showCreateProfileModal, setShowCreateProfileModal] = useState(showCreateProfileModalFromHome);
    const [showManageProfilesModal, setShowManageProfilesModal] = useState(false);
    const [showUserProfileEditModal, setShowUserProfileEditModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [collapsedGroups, setCollapsedGroups] = useState({});

    useEffect(() => {
        setShowCreateProfileModal(showCreateProfileModalFromHome);
    }, [showCreateProfileModalFromHome]);

    const logActivity = async (activityType, profileId = null) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/log_activity`, { activity_type: activityType, profile_id: profileId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch (error) {
            console.error("Error logging activity:", error);
        }
    };

    const handleOpenManageProfilesModal = () => {
        setShowManageProfilesModal(true);
        logActivity(ActivityType.PROFILE_MANAGEMENT_MODAL_OPENED, activeProfileId);
    };

    const handleCloseManageProfilesModal = () => {
        setShowManageProfilesModal(false);
        logActivity(ActivityType.PROFILE_MANAGEMENT_MODAL_CLOSED, activeProfileId);
    };

    const handleCloseCreateProfileModal = () => {
        setShowCreateProfileModal(false);
        setShowCreateProfileModalFromHome(false);
    };

    const handleProfileClick = (profileId) => {
        onProfileSelect(profileId);
    };

    const toggleGroup = (groupType) => {
        setCollapsedGroups(prev => ({
            ...prev,
            [groupType]: !prev[groupType]
        }));
    };

    const visibleProfiles = profiles.filter(profile => !profile.is_hidden);
    const groupedProfiles = visibleProfiles.reduce((acc, profile) => {
        const type = profile.profile_type || 'UNCATEGORIZED';
        if (!acc[type]) {
            acc[type] = [];
        }
        acc[type].push(profile);
        return acc;
    }, {});

    const getProfileTypeIcon = (profileType) => {
        switch (profileType) {
            case 'EXPENSE_MANAGER':
                return <Briefcase size={16} />;
            case 'ASSET_MANAGER':
                return <GraphUp size={16} />;
            case 'LIFE_MANAGER':
                return <CheckSquare size={16} />;
            case 'MARKET_WATCH':
                return <GraphUpArrow size={16} />;
            default:
                return null;
        }
    };

    const getProfileTypeName = (profileType) => {
        switch (profileType) {
            case 'EXPENSE_MANAGER':
                return 'Expenses';
            case 'ASSET_MANAGER':
                return 'Assets';
            case 'LIFE_MANAGER':
                return 'Life Management';
            case 'MARKET_WATCH':
                return 'Market Watch';
            default:
                return profileType.replace('_', ' ');
        }
    };

    return (
        <div className="modern-sidebar">
            <div className="sidebar-header">
                <h6 className="sidebar-title">Profiles</h6>
            </div>
            
            <div className="sidebar-content">
                <div className="home-section">
                    <div 
                        className={`home-item ${activeProfileId === null ? 'active' : ''}`}
                        onClick={() => onHomeSelect()}
                    >
                        <div className="home-info">
                            🏠 <span className="home-name">Home</span>
                        </div>
                        {activeProfileId === null && <div className="active-indicator" />}
                    </div>
                </div>

                {Object.entries(groupedProfiles).map(([type, profilesInGroup]) => (
                    <div key={type} className="profile-group">
                        <div 
                            className="group-header" 
                            onClick={() => toggleGroup(type)}
                        >
                            <div className="group-info">
                                {getProfileTypeIcon(type)}
                                <span className="group-name">{getProfileTypeName(type)}</span>
                                <Badge bg="secondary" className="profile-count">{profilesInGroup.length}</Badge>
                            </div>
                            <ChevronDown 
                                size={14} 
                                className={`chevron ${collapsedGroups[type] ? 'collapsed' : ''}`}
                            />
                        </div>
                        
                        <div className={`profile-list ${collapsedGroups[type] ? 'collapsed' : ''}`}>
                            {profilesInGroup.map(profile => (
                                <div
                                    key={profile.id}
                                    onClick={() => handleProfileClick(profile.id)}
                                    className={`profile-item ${profile.id === activeProfileId ? 'active' : ''}`}
                                >
                                    <div className="profile-info">
                                        <span className="profile-flag">{getFlagEmoji(profile.currency)}</span>
                                        <span className="profile-name">{profile.name}</span>
                                    </div>
                                    {profile.id === activeProfileId && <div className="active-indicator" />}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            <div className="sidebar-footer">
                <button 
                    className="modern-btn primary-btn" 
                    onClick={() => setShowCreateProfileModal(true)}
                >
                    + New Profile
                </button>
                
                <Dropdown className="settings-dropdown">
                    <Dropdown.Toggle as="button" className="modern-btn secondary-btn">
                        ⚙ Settings
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={handleOpenManageProfilesModal}>
                            ⚙ Manage Profiles
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setShowUserProfileEditModal(true)}>
                            ✏ Edit Profile
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setShowChangePasswordModal(true)}>
                            🔑 Change Password
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>

            <CreateProfileModal
                show={showCreateProfileModal}
                handleClose={handleCloseCreateProfileModal}
                handleCreateProfile={handleCreateProfile}
            />
            <ManageProfilesModal
                show={showManageProfilesModal}
                handleClose={handleCloseManageProfilesModal}
                profiles={profiles}
                onProfileVisibilityChange={onProfileVisibilityChange}
                handleUpdateProfile={handleUpdateProfile}
                handleDeleteProfile={handleDeleteProfile}
            />
            <UserProfileEditModal
                show={showUserProfileEditModal}
                handleClose={() => setShowUserProfileEditModal(false)}
            />
            <ChangePasswordModal
                show={showChangePasswordModal}
                handleClose={() => setShowChangePasswordModal(false)}
            />
        </div>
    );
};

export default SideBar;
